import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, TextField, MenuItem,
  Grid2 as Grid, CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, IconButton, Tabs, Tab, Switch, FormControlLabel,
  Tooltip, Divider,
} from '@mui/material';
import {
  Visibility, Refresh, CheckCircle, Cancel, HourglassEmpty,
  CallReceived, TrendingUp, Autorenew, ErrorOutline,
  Login as LoginIcon, PhoneAndroid, Computer, PersonAdd,
  LockOpen, Warning, Info, BugReport, CrisisAlert,
  Storage,
} from '@mui/icons-material';
import api from '../../../services/api';

// ─── helpers ────────────────────────────────────────────────────────────────

const STATUS_PEDIDO: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' }> = {
  N: { label: 'Sucesso',   color: 'success' },
  F: { label: 'Falhado',   color: 'error'   },
  S: { label: 'Pendente',  color: 'warning' },
  E: { label: 'Estornado', color: 'info'    },
};

// ─── login event helpers ─────────────────────────────────────────────────────

const LOGIN_TIPO_COLOR: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  LOGIN_SUCCESS:    'success',
  GOOGLE_LOGIN:     'success',
  APPLE_LOGIN:      'success',
  REGISTER_SUCCESS: 'info',
  LOGIN_FAILED:     'error',
  REGISTER_FAILED:  'error',
  ACCOUNT_LOCKED:   'error',
  PASSWORD_RESET:   'warning',
  TOKEN_REFRESH:    'default',
  LOGOUT:           'default',
  SESSION_EXPIRED:  'warning',
};

const LOGIN_TIPO_LABEL: Record<string, string> = {
  LOGIN_SUCCESS:    'Login OK',
  GOOGLE_LOGIN:     'Google Login',
  APPLE_LOGIN:      'Apple Login',
  REGISTER_SUCCESS: 'Registo OK',
  LOGIN_FAILED:     'Login Falhado',
  REGISTER_FAILED:  'Registo Falhou',
  ACCOUNT_LOCKED:   'Conta Bloqueada',
  PASSWORD_RESET:   'Reset Password',
  TOKEN_REFRESH:    'Token Refresh',
  LOGOUT:           'Logout',
  SESSION_EXPIRED:  'Sessão Expirada',
};

// ─── sistema/Pagali log helpers ──────────────────────────────────────────────

const NIVEL_COLOR: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  DEBUG:    'default',
  INFO:     'info',
  WARN:     'warning',
  ERROR:    'error',
  CRITICAL: 'error',
};

const NIVEL_ICON: Record<string, React.ReactNode> = {
  DEBUG:    <BugReport fontSize="small" />,
  INFO:     <Info fontSize="small" />,
  WARN:     <Warning fontSize="small" />,
  ERROR:    <Cancel fontSize="small" />,
  CRITICAL: <CrisisAlert fontSize="small" />,
};

const TIPO_LOG_COLOR: Record<string, 'info' | 'primary' | 'success' | 'secondary' | 'default'> = {
  CALLBACK:        'success',
  REFUND_CALLBACK: 'info',
  REQUEST:         'primary',
  RESPONSE:        'default',
  TOKEN_REQUEST:   'secondary',
  TOKEN_CALLBACK:  'secondary',
  TOKEN_PAYMENT:   'secondary',
};

const fmt = (n: number) =>
  new Intl.NumberFormat('pt-CV', { minimumFractionDigits: 0 }).format(n);

const fmtDate = (d: string | undefined) =>
  d ? new Date(d).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', dateStyle: 'short', timeStyle: 'short' }) : '-';

// ─── stat card ───────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  color: string;
}
const StatCard: React.FC<StatCardProps> = ({ label, value, sub, icon, color }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{
        bgcolor: `${color}15`, color, borderRadius: 1.5, p: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>{label}</Typography>
        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </Box>
    </Box>
  </Paper>
);

// ─── main component ──────────────────────────────────────────────────────────

const SispLogs: React.FC = () => {
  const [tab, setTab] = useState(0);

  // ── stats
  const [stats, setStats]           = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [period, setPeriod]         = useState('7');

  // ── transactions (pg_sisp_pedido)
  const [txs, setTxs]               = useState<any[]>([]);
  const [txLoading, setTxLoading]   = useState(false);
  const [txPage, setTxPage]         = useState(0);
  const [txRows, setTxRows]         = useState(25);
  const [txTotal, setTxTotal]       = useState(0);
  const [txStatus, setTxStatus]     = useState('');
  const [txSearch, setTxSearch]     = useState('');
  const [txStart, setTxStart]       = useState('');
  const [txEnd, setTxEnd]           = useState('');

  // ── logs (pg_sisp_log)
  const [logs, setLogs]             = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage]     = useState(0);
  const [logsRows, setLogsRows]     = useState(25);
  const [logsTotal, setLogsTotal]   = useState(0);
  const [logTipo, setLogTipo]       = useState('');
  const [logStart, setLogStart]     = useState('');
  const [logEnd, setLogEnd]         = useState('');
  const [callbackTotal, setCallbackTotal] = useState<number | null>(null);

  // ── login logs (tab 3)
  const [loginLogs, setLoginLogs]         = useState<any[]>([]);
  const [loginLoading, setLoginLoading]   = useState(false);
  const [loginPage, setLoginPage]         = useState(0);
  const [loginRows, setLoginRows]         = useState(25);
  const [loginTotal, setLoginTotal]       = useState(0);
  const [loginSearch, setLoginSearch]     = useState('');
  const [loginTipo, setLoginTipo]         = useState('');
  const [loginDevice, setLoginDevice]     = useState('');
  const [loginStart, setLoginStart]       = useState('');
  const [loginEnd, setLoginEnd]           = useState('');
  const [loginStats, setLoginStats]       = useState<any>(null);

  // ── sistema logs (tab 4)
  const [sysLogs, setSysLogs]           = useState<any[]>([]);
  const [sysLoading, setSysLoading]     = useState(false);
  const [sysPage, setSysPage]           = useState(0);
  const [sysRows, setSysRows]           = useState(50);
  const [sysTotal, setSysTotal]         = useState(0);
  const [sysSearch, setSysSearch]       = useState('');
  const [sysNivel, setSysNivel]         = useState('');
  const [sysModulo, setSysModulo]       = useState('');
  const [sysStart, setSysStart]         = useState('');
  const [sysEnd, setSysEnd]             = useState('');
  const [sysStats, setSysStats]         = useState<any>(null);
  const [modulos, setModulos]           = useState<string[]>([]);

  // ── e-commerce logs (tab 5)
  const [ecLogs, setEcLogs]             = useState<any[]>([]);
  const [ecLoading, setEcLoading]       = useState(false);
  const [ecPage, setEcPage]             = useState(0);
  const [ecRows, setEcRows]             = useState(50);
  const [ecTotal, setEcTotal]           = useState(0);
  const [ecStatus, setEcStatus]         = useState('');
  const [ecSearch, setEcSearch]         = useState('');
  const [ecEntidade, setEcEntidade]     = useState('');
  const [ecStart, setEcStart]           = useState('');
  const [ecEnd, setEcEnd]               = useState('');

  // ── common
  const [error, setError]           = useState<string | null>(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── fetch stats ─────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [statsRes, cbRes] = await Promise.all([
        api.get(`/admin/sisp/payments/stats?period=${period}`),
        api.get(`/admin/sisp/logs?tipo=CALLBACK&limit=1`),
      ]);
      if (statsRes.data.success) setStats(statsRes.data.data);
      if (cbRes.data.success) setCallbackTotal(cbRes.data.pagination?.total ?? null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setStatsLoading(false);
    }
  }, [period]);

  // ── fetch transactions ───────────────────────────────────────────────────────
  const fetchTxs = useCallback(async () => {
    setTxLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(txPage + 1),
        limit: String(txRows),
      });
      if (txStatus) params.append('status', txStatus);
      if (txStart)  params.append('startDate', txStart);
      if (txEnd)    params.append('endDate', txEnd);
      const res = await api.get(`/admin/sisp/payments?${params}`);
      if (res.data.success) {
        // filter by search client-side (merchantRef or customerName)
        const data: any[] = res.data.data || [];
        const filtered = txSearch
          ? data.filter(r =>
              (r.merchantRef || '').toLowerCase().includes(txSearch.toLowerCase()) ||
              (r.customerName || '').toLowerCase().includes(txSearch.toLowerCase()) ||
              (r.invoiceReference || '').toLowerCase().includes(txSearch.toLowerCase())
            )
          : data;
        setTxs(filtered);
        setTxTotal(res.data.pagination?.total || data.length);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setTxLoading(false);
    }
  }, [txPage, txRows, txStatus, txStart, txEnd, txSearch]);

  // ── fetch logs ───────────────────────────────────────────────────────────────
  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(logsPage + 1),
        limit: String(logsRows),
      });
      if (logTipo)  params.append('tipo', logTipo);
      if (logStart) params.append('startDate', logStart);
      if (logEnd)   params.append('endDate', logEnd);
      const res = await api.get(`/admin/sisp/logs?${params}`);
      if (res.data.success) {
        setLogs(res.data.data || []);
        setLogsTotal(res.data.pagination?.total || 0);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLogsLoading(false);
    }
  }, [logsPage, logsRows, logTipo, logStart, logEnd]);

  // ── fetch login logs ─────────────────────────────────────────────────────────
  const fetchLoginLogs = useCallback(async () => {
    setLoginLoading(true);
    try {
      const params = new URLSearchParams({ page: String(loginPage + 1), limit: String(loginRows) });
      if (loginSearch) params.append('search', loginSearch);
      if (loginTipo)   params.append('tipo', loginTipo);
      if (loginDevice) params.append('device', loginDevice);
      if (loginStart)  params.append('startDate', loginStart);
      if (loginEnd)    params.append('endDate', loginEnd);
      const res = await api.get(`/admin/logs/login?${params}`);
      if (res.data.success) {
        setLoginLogs(res.data.data || []);
        setLoginTotal(res.data.pagination?.total || 0);
        setLoginStats(res.data.stats || null);
      }
    } catch (e: any) { setError(e.message); }
    finally { setLoginLoading(false); }
  }, [loginPage, loginRows, loginSearch, loginTipo, loginDevice, loginStart, loginEnd]);

  // ── fetch sistema logs ────────────────────────────────────────────────────────
  const fetchSysLogs = useCallback(async () => {
    setSysLoading(true);
    try {
      const params = new URLSearchParams({ page: String(sysPage + 1), limit: String(sysRows) });
      if (sysSearch) params.append('search', sysSearch);
      if (sysNivel)  params.append('nivel', sysNivel);
      if (sysModulo) params.append('modulo', sysModulo);
      if (sysStart)  params.append('startDate', sysStart);
      if (sysEnd)    params.append('endDate', sysEnd);
      const [res, modRes] = await Promise.all([
        api.get(`/admin/logs/sistema?${params}`),
        modulos.length === 0 ? api.get('/admin/logs/modulos') : Promise.resolve(null),
      ]);
      if (res.data.success) {
        setSysLogs(res.data.data || []);
        setSysTotal(res.data.pagination?.total || 0);
        setSysStats(res.data.stats || null);
      }
      if (modRes?.data?.success) setModulos(modRes.data.data || []);
    } catch (e: any) { setError(e.message); }
    finally { setSysLoading(false); }
  }, [sysPage, sysRows, sysSearch, sysNivel, sysModulo, sysStart, sysEnd, modulos.length]);

  // ── fetch e-commerce logs ────────────────────────────────────────────────────
  const fetchEcLogs = useCallback(async () => {
    setEcLoading(true);
    try {
      const params = new URLSearchParams({ page: String(ecPage + 1), limit: String(ecRows) });
      if (ecStatus)   params.append('status',    ecStatus);
      if (ecSearch)   params.append('search',    ecSearch);
      if (ecEntidade) params.append('entidade',  ecEntidade);
      if (ecStart)    params.append('startDate', ecStart);
      if (ecEnd)      params.append('endDate',   ecEnd);
      const res = await api.get(`/admin/sisp/ecommerce-logs?${params}`);
      if (res.data.success) {
        setEcLogs(res.data.data || []);
        setEcTotal(res.data.pagination?.total || 0);
      }
    } catch (e: any) { setError(e.message); }
    finally { setEcLoading(false); }
  }, [ecPage, ecRows, ecStatus, ecSearch, ecEntidade, ecStart, ecEnd]);

  // ── effects ──────────────────────────────────────────────────────────────────
  useEffect(() => { if (tab === 0) fetchStats();     }, [tab, fetchStats]);
  useEffect(() => { if (tab === 1) fetchTxs();       }, [tab, fetchTxs]);
  useEffect(() => { if (tab === 2) fetchLogs();      }, [tab, fetchLogs]);
  useEffect(() => { if (tab === 3) fetchLoginLogs(); }, [tab, fetchLoginLogs]);
  useEffect(() => { if (tab === 4) fetchSysLogs();   }, [tab, fetchSysLogs]);
  useEffect(() => { if (tab === 5) fetchEcLogs();    }, [tab, fetchEcLogs]);

  // auto-refresh every 30s
  useEffect(() => {
    if (!autoRefresh) { if (timerRef.current) clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => {
      if (tab === 0) fetchStats();
      else if (tab === 1) fetchTxs();
      else if (tab === 2) fetchLogs();
      else if (tab === 3) fetchLoginLogs();
      else if (tab === 4) fetchSysLogs();
      else fetchEcLogs();
    }, 30_000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoRefresh, tab, fetchStats, fetchTxs, fetchLogs, fetchLoginLogs, fetchSysLogs, fetchEcLogs]);

  // ── detail view ──────────────────────────────────────────────────────────────
  const openDetail = async (item: any, isLog: boolean) => {
    setSelectedItem(null);
    setDetailDialog(true);
    setDetailLoading(true);
    try {
      if (isLog) {
        const res = await api.get(`/admin/sisp/logs/${item.id}`);
        setSelectedItem(res.data.success ? { ...res.data.data, _isLog: true } : { ...item, _isLog: true });
      } else {
        const res = await api.get(`/admin/sisp/payments/${item.id}`);
        setSelectedItem(res.data.success ? res.data.data : item);
      }
    } catch {
      setSelectedItem(isLog ? { ...item, _isLog: true } : item);
    } finally {
      setDetailLoading(false);
    }
  };

  // ── KPI derived values ───────────────────────────────────────────────────────
  const summary = stats?.summary;
  const successRate = summary?.total > 0
    ? Math.round((summary.success / summary.total) * 100) : 0;

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Logs do Sistema</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={<Switch size="small" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />}
            label={<Typography variant="caption">Auto-refresh 30s</Typography>}
            sx={{ mr: 0 }}
          />
          <Tooltip title="Actualizar">
            <IconButton size="small" onClick={() => {
              if (tab === 0) fetchStats();
              else if (tab === 1) fetchTxs();
              else if (tab === 2) fetchLogs();
              else if (tab === 3) fetchLoginLogs();
              else if (tab === 4) fetchSysLogs();
              else fetchEcLogs();
            }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Monitorização" />
        <Tab label="Transações" />
        <Tab label="Logs Comunicação" />
        <Tab icon={<LoginIcon fontSize="small" />} iconPosition="start" label="Login" sx={{ textTransform: 'none' }} />
        <Tab icon={<Storage fontSize="small" />} iconPosition="start" label="Pagali" sx={{ textTransform: 'none' }} />
        <Tab icon={<ErrorOutline fontSize="small" />} iconPosition="start" label="E-Commerce" sx={{ textTransform: 'none' }} />
      </Tabs>

      {/* ══════ TAB 0: MONITORIZAÇÃO ══════ */}
      {tab === 0 && (
        <Box>
          {/* period selector */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3, alignItems: 'center' }}>
            {['7', '30', '90'].map(d => (
              <Button key={d} size="small"
                variant={period === d ? 'contained' : 'outlined'}
                onClick={() => setPeriod(d)}
              >
                {d}d
              </Button>
            ))}
            {statsLoading && <CircularProgress size={18} sx={{ ml: 1 }} />}
          </Box>

          {/* KPI cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                label="Total" value={fmt(summary?.total ?? 0)}
                sub={`${period}d`}
                icon={<TrendingUp fontSize="small" />} color="#6B2FA0"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                label="Sucesso" value={fmt(summary?.success ?? 0)}
                sub={`${successRate}% de sucesso`}
                icon={<CheckCircle fontSize="small" />} color="#2e7d32"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                label="Pendentes" value={fmt(summary?.pending ?? 0)}
                icon={<HourglassEmpty fontSize="small" />} color="#ed6c02"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                label="Falhadas" value={fmt(summary?.failed ?? 0)}
                icon={<Cancel fontSize="small" />} color="#d32f2f"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                label="Callbacks" value={callbackTotal !== null ? fmt(callbackTotal) : '—'}
                sub="recebidos (total)"
                icon={<CallReceived fontSize="small" />} color="#0288d1"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <StatCard
                label="Volume" value={`${fmt(Math.round((summary?.successAmount ?? 0) / 100))} CVE`}
                sub="pagamentos confirmados"
                icon={<Autorenew fontSize="small" />} color="#1976d2"
              />
            </Grid>
          </Grid>

          {/* Daily breakdown */}
          {stats?.dailyPayments?.length > 0 && (
            <Paper variant="outlined" sx={{ mb: 3 }}>
              <Box sx={{ p: 2, pb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>Evolução diária (últimos 7 dias)</Typography>
              </Box>
              <Divider />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell>Data</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Sucesso</TableCell>
                      <TableCell align="right">Falhado</TableCell>
                      <TableCell align="right">Taxa</TableCell>
                      <TableCell align="right">Volume (CVE)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.dailyPayments.map((row: any) => {
                      const rate = row.total > 0 ? Math.round((row.success / row.total) * 100) : 0;
                      return (
                        <TableRow key={row.date} hover>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {new Date(row.date).toLocaleDateString('pt-PT', { timeZone: 'UTC', weekday: 'short', day: '2-digit', month: 'short' })}
                          </TableCell>
                          <TableCell align="right">{row.total}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>{row.success}</TableCell>
                          <TableCell align="right" sx={{ color: row.failed > 0 ? 'error.main' : 'text.secondary' }}>{row.failed}</TableCell>
                          <TableCell align="right">
                            <Box sx={{
                              display: 'inline-block', px: 1, py: 0.25, borderRadius: 1, fontSize: 12, fontWeight: 600,
                              bgcolor: rate >= 80 ? 'success.50' : rate >= 50 ? 'warning.50' : 'error.50',
                              color: rate >= 80 ? 'success.dark' : rate >= 50 ? 'warning.dark' : 'error.dark',
                            }}>
                              {rate}%
                            </Box>
                          </TableCell>
                          <TableCell align="right">{fmt(Math.round((row.amount || 0) / 100))}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Method + Origin breakdown */}
          <Grid container spacing={2}>
            {stats?.methodStats?.length > 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined">
                  <Box sx={{ p: 2, pb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>Por método de pagamento</Typography>
                  </Box>
                  <Divider />
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell>Método</TableCell>
                          <TableCell align="right">Transações</TableCell>
                          <TableCell align="right">Volume (CVE)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.methodStats.map((m: any) => (
                          <TableRow key={m.method || '-'} hover>
                            <TableCell>
                              <Chip label={m.method || 'N/D'} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{m.count}</TableCell>
                            <TableCell align="right">{fmt(Math.round((m.amount || 0) / 100))}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            )}
            {stats?.originStats?.length > 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined">
                  <Box sx={{ p: 2, pb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>Por origem</Typography>
                  </Box>
                  <Divider />
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell>Origem</TableCell>
                          <TableCell align="right">Transações</TableCell>
                          <TableCell align="right">Volume (CVE)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.originStats.map((o: any) => (
                          <TableRow key={o.origin || '-'} hover>
                            <TableCell>
                              <Chip label={o.origin || 'N/D'} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{o.count}</TableCell>
                            <TableCell align="right">{fmt(Math.round((o.amount || 0) / 100))}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            )}
          </Grid>

          {!statsLoading && !stats && (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <ErrorOutline sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
              <Typography>Sem dados de monitorização</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ══════ TAB 1: TRANSAÇÕES ══════ */}
      {tab === 1 && (
        <Box>
          {/* Filters */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField fullWidth size="small" label="Pesquisar" placeholder="MerchantRef, cliente…"
                value={txSearch} onChange={e => { setTxSearch(e.target.value); setTxPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" select label="Status"
                value={txStatus} onChange={e => { setTxStatus(e.target.value); setTxPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="N">Sucesso</MenuItem>
                <MenuItem value="S">Pendente</MenuItem>
                <MenuItem value="F">Falhado</MenuItem>
                <MenuItem value="E">Estornado</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" type="date" label="De"
                InputLabelProps={{ shrink: true }}
                value={txStart} onChange={e => { setTxStart(e.target.value); setTxPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" type="date" label="Até"
                InputLabelProps={{ shrink: true }}
                value={txEnd} onChange={e => { setTxEnd(e.target.value); setTxPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 1 }}>
              <Button fullWidth variant="outlined" size="small" sx={{ height: 40 }}
                onClick={() => { setTxSearch(''); setTxStatus(''); setTxStart(''); setTxEnd(''); setTxPage(0); }}>
                Limpar
              </Button>
            </Grid>
          </Grid>

          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>MerchantRef</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Método</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Entidade</TableCell>
                    <TableCell>Erro</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="center" width={40} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {txLoading ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                  ) : txs.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nenhuma transação encontrada
                    </TableCell></TableRow>
                  ) : txs.map((tx) => {
                    const st = STATUS_PEDIDO[tx.status] || { label: tx.status || '-', color: 'default' };
                    return (
                      <TableRow key={tx.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                          {tx.merchantRef || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {tx.amount ? `${fmt(Math.round(tx.amount / 100))} CVE` : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip label={st.label} size="small" color={st.color as any} />
                        </TableCell>
                        <TableCell>
                          <Chip label={tx.paymentMethod || tx.origin || '-'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{tx.customerName || '-'}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{tx.entityName || '-'}</TableCell>
                        <TableCell sx={{ fontSize: 11, color: 'error.main', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Tooltip title={tx.errorDescription || ''} placement="top">
                            <span>{tx.errorCode ? `${tx.errorCode}${tx.errorDescription ? ` – ${tx.errorDescription}` : ''}` : ''}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(tx.createdAt)}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => openDetail(tx, false)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={txTotal} page={txPage}
              onPageChange={(_, p) => setTxPage(p)} rowsPerPage={txRows}
              onRowsPerPageChange={e => { setTxRows(parseInt(e.target.value)); setTxPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Linhas:" />
          </Paper>
        </Box>
      )}

      {/* ══════ TAB 2: LOGS COMUNICAÇÃO ══════ */}
      {tab === 2 && (
        <Box>
          {callbackTotal !== null && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Total de callbacks recebidos (todos os tempos): <strong>{fmt(callbackTotal)}</strong>
              {callbackTotal === 0 && ' — nenhum callback registado ainda. Verifique se o SISP está a conseguir contactar a API.'}
            </Alert>
          )}

          {/* Filters */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" select label="Tipo"
                value={logTipo} onChange={e => { setLogTipo(e.target.value); setLogsPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="CALLBACK">Callback</MenuItem>
                <MenuItem value="REFUND_CALLBACK">Refund Callback</MenuItem>
                <MenuItem value="REQUEST">Request</MenuItem>
                <MenuItem value="RESPONSE">Response</MenuItem>
                <MenuItem value="TOKEN_REQUEST">Token Request</MenuItem>
                <MenuItem value="TOKEN_CALLBACK">Token Callback</MenuItem>
                <MenuItem value="TOKEN_PAYMENT">Token Payment</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" type="date" label="De"
                InputLabelProps={{ shrink: true }}
                value={logStart} onChange={e => { setLogStart(e.target.value); setLogsPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" type="date" label="Até"
                InputLabelProps={{ shrink: true }}
                value={logEnd} onChange={e => { setLogEnd(e.target.value); setLogsPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 1 }}>
              <Button fullWidth variant="outlined" size="small" sx={{ height: 40 }}
                onClick={() => { setLogTipo(''); setLogStart(''); setLogEnd(''); setLogsPage(0); }}>
                Limpar
              </Button>
            </Grid>
          </Grid>

          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>Tipo</TableCell>
                    <TableCell>MerchantRef</TableCell>
                    <TableCell>Método</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell>Duração</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="center" width={40} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logsLoading ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                  ) : logs.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nenhum log encontrado
                    </TableCell></TableRow>
                  ) : logs.map((log) => {
                    const isCallback = log.tipo?.includes('CALLBACK');
                    return (
                      <TableRow key={log.id} hover
                        sx={isCallback ? { bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' } } : {}}>
                        <TableCell>
                          <Chip label={log.tipo || '-'} size="small"
                            color={(TIPO_LOG_COLOR[log.tipo] || 'default') as any} />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{log.merchantRef || '-'}</TableCell>
                        <TableCell>{log.metodo || '-'}</TableCell>
                        <TableCell sx={{ fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Tooltip title={log.url || ''} placement="top"><span>{log.url || '-'}</span></Tooltip>
                        </TableCell>
                        <TableCell>
                          {log.responseCode
                            ? <Chip label={log.responseCode} size="small"
                                color={log.responseCode >= 200 && log.responseCode < 300 ? 'success' : 'error'} />
                            : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {log.duracao ? `${log.duracao} ms` : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(log.createdAt)}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => openDetail(log, true)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={logsTotal} page={logsPage}
              onPageChange={(_, p) => setLogsPage(p)} rowsPerPage={logsRows}
              onRowsPerPageChange={e => { setLogsRows(parseInt(e.target.value)); setLogsPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Linhas:" />
          </Paper>
        </Box>
      )}

      {/* ══════ TAB 3: LOGIN LOGS ══════ */}
      {tab === 3 && (
        <Box>
          {/* Stats row */}
          {loginStats?.today && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <StatCard label="Logins hoje" value={fmt(loginStats.today.sucesso ?? 0)}
                  icon={<CheckCircle fontSize="small" />} color="#2e7d32" />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <StatCard label="Falhas hoje" value={fmt(loginStats.today.falha ?? 0)}
                  icon={<Cancel fontSize="small" />} color="#d32f2f" />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <StatCard label="Registos hoje" value={fmt(loginStats.today.registos ?? 0)}
                  icon={<PersonAdd fontSize="small" />} color="#0288d1" />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <StatCard label="Utilizadores únicos" value={fmt(loginStats.today.unique_users ?? 0)}
                  icon={<LockOpen fontSize="small" />} color="#6B2FA0" />
              </Grid>
            </Grid>
          )}

          {/* Mini chart — daily last 7 days */}
          {loginStats?.daily?.length > 0 && (
            <Paper variant="outlined" sx={{ mb: 2 }}>
              <Box sx={{ p: 2, pb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>Evolução últimos 7 dias</Typography>
              </Box>
              <Divider />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell>Data</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>Sucesso</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>Falhas</TableCell>
                      <TableCell align="right" sx={{ color: 'info.main' }}>Registos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loginStats.daily.map((row: any) => (
                      <TableRow key={row.data} hover>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {new Date(row.data).toLocaleDateString('pt-PT', { timeZone: 'UTC', weekday: 'short', day: '2-digit', month: 'short' })}
                        </TableCell>
                        <TableCell align="right">{row.total}</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>{row.sucesso}</TableCell>
                        <TableCell align="right" sx={{ color: row.falha > 0 ? 'error.main' : 'text.secondary' }}>{row.falha}</TableCell>
                        <TableCell align="right" sx={{ color: 'info.main' }}>{row.registos}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Filters */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField fullWidth size="small" label="Pesquisar" placeholder="Email, nome…"
                value={loginSearch} onChange={e => { setLoginSearch(e.target.value); setLoginPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" select label="Tipo"
                value={loginTipo} onChange={e => { setLoginTipo(e.target.value); setLoginPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="LOGIN_SUCCESS">Login OK</MenuItem>
                <MenuItem value="LOGIN_FAILED">Login Falhado</MenuItem>
                <MenuItem value="REGISTER_SUCCESS">Registo OK</MenuItem>
                <MenuItem value="REGISTER_FAILED">Registo Falhou</MenuItem>
                <MenuItem value="GOOGLE_LOGIN">Google Login</MenuItem>
                <MenuItem value="APPLE_LOGIN">Apple Login</MenuItem>
                <MenuItem value="ACCOUNT_LOCKED">Conta Bloqueada</MenuItem>
                <MenuItem value="PASSWORD_RESET">Reset Password</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" select label="Dispositivo"
                value={loginDevice} onChange={e => { setLoginDevice(e.target.value); setLoginPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="MOB">Mobile</MenuItem>
                <MenuItem value="WEB">Web</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" type="date" label="De"
                InputLabelProps={{ shrink: true }}
                value={loginStart} onChange={e => { setLoginStart(e.target.value); setLoginPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" type="date" label="Até"
                InputLabelProps={{ shrink: true }}
                value={loginEnd} onChange={e => { setLoginEnd(e.target.value); setLoginPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 1 }}>
              <Button fullWidth variant="outlined" size="small" sx={{ height: 40 }}
                onClick={() => { setLoginSearch(''); setLoginTipo(''); setLoginDevice(''); setLoginStart(''); setLoginEnd(''); setLoginPage(0); }}>
                Limpar
              </Button>
            </Grid>
          </Grid>

          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Utilizador</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Dispositivo</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell>User-Agent</TableCell>
                    <TableCell>Erro</TableCell>
                    <TableCell>Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loginLoading ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                  ) : loginLogs.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      <LoginIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3, display: 'block', mx: 'auto' }} />
                      Nenhum log de login encontrado.<br />Os registos aparecerão aqui após o primeiro login.
                    </TableCell></TableRow>
                  ) : loginLogs.map((entry) => (
                    <TableRow key={entry.id} hover
                      sx={entry.tipo?.includes('FAILED') || entry.tipo === 'ACCOUNT_LOCKED'
                        ? { bgcolor: 'error.50' } : {}}>
                      <TableCell>
                        <Chip label={LOGIN_TIPO_LABEL[entry.tipo] || entry.tipo} size="small"
                          color={(LOGIN_TIPO_COLOR[entry.tipo] || 'default') as any} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{entry.nome || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{entry.email || '—'}</TableCell>
                      <TableCell>
                        {entry.device_type === 'MOB'
                          ? <Chip icon={<PhoneAndroid sx={{ fontSize: 14 }} />} label="Mobile" size="small" variant="outlined" />
                          : <Chip icon={<Computer sx={{ fontSize: 14 }} />} label="Web" size="small" variant="outlined" />}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{entry.ip_address || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 10, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={entry.user_agent || ''} placement="top">
                          <span>{entry.user_agent || '—'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'error.main' }}>
                        {entry.error_code ? `${entry.error_code}${entry.error_msg ? ` – ${entry.error_msg}` : ''}` : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(entry.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={loginTotal} page={loginPage}
              onPageChange={(_, p) => setLoginPage(p)} rowsPerPage={loginRows}
              onRowsPerPageChange={e => { setLoginRows(parseInt(e.target.value)); setLoginPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Linhas:" />
          </Paper>
        </Box>
      )}

      {/* ══════ TAB 4: PAGALI (SYSTEM LOGS) ══════ */}
      {tab === 4 && (
        <Box>
          {/* Stats row */}
          {sysStats?.recent24 && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <StatCard label="Eventos (24h)" value={fmt(sysStats.recent24.total ?? 0)}
                  icon={<Storage fontSize="small" />} color="#6B2FA0" />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <StatCard label="Erros (24h)" value={fmt(sysStats.recent24.errors ?? 0)}
                  sub={sysStats.recent24.criticals > 0 ? `${sysStats.recent24.criticals} críticos` : undefined}
                  icon={<Cancel fontSize="small" />} color="#d32f2f" />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <StatCard label="Avisos (24h)" value={fmt(sysStats.recent24.warnings ?? 0)}
                  icon={<Warning fontSize="small" />} color="#ed6c02" />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <StatCard label="Módulos activos" value={sysStats.moduloStats?.length ?? 0}
                  icon={<Info fontSize="small" />} color="#0288d1" />
              </Grid>
            </Grid>
          )}

          {/* Breakdown by module */}
          {sysStats?.moduloStats?.length > 0 && (
            <Paper variant="outlined" sx={{ mb: 2 }}>
              <Box sx={{ p: 2, pb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>Por módulo (últimas 24h)</Typography>
              </Box>
              <Divider />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell>Módulo</TableCell>
                      <TableCell align="right">Eventos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sysStats.moduloStats.map((m: any) => (
                      <TableRow key={m.modulo} hover>
                        <TableCell><Chip label={m.modulo} size="small" variant="outlined" /></TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{m.total}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Filters */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 4, md: 3 }}>
              <TextField fullWidth size="small" label="Pesquisar" placeholder="Evento, mensagem, módulo…"
                value={sysSearch} onChange={e => { setSysSearch(e.target.value); setSysPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" select label="Nível"
                value={sysNivel} onChange={e => { setSysNivel(e.target.value); setSysPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="DEBUG">Debug</MenuItem>
                <MenuItem value="INFO">Info</MenuItem>
                <MenuItem value="WARN">Aviso</MenuItem>
                <MenuItem value="ERROR">Erro</MenuItem>
                <MenuItem value="CRITICAL">Crítico</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" select label="Módulo"
                value={sysModulo} onChange={e => { setSysModulo(e.target.value); setSysPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                {modulos.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" type="date" label="De"
                InputLabelProps={{ shrink: true }}
                value={sysStart} onChange={e => { setSysStart(e.target.value); setSysPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <TextField fullWidth size="small" type="date" label="Até"
                InputLabelProps={{ shrink: true }}
                value={sysEnd} onChange={e => { setSysEnd(e.target.value); setSysPage(0); }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 1 }}>
              <Button fullWidth variant="outlined" size="small" sx={{ height: 40 }}
                onClick={() => { setSysSearch(''); setSysNivel(''); setSysModulo(''); setSysStart(''); setSysEnd(''); setSysPage(0); }}>
                Limpar
              </Button>
            </Grid>
          </Grid>

          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell width={90}>Nível</TableCell>
                    <TableCell>Módulo</TableCell>
                    <TableCell>Evento</TableCell>
                    <TableCell>Mensagem</TableCell>
                    <TableCell>Utilizador</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell>Duração</TableCell>
                    <TableCell>Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sysLoading ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                  ) : sysLogs.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      <Storage sx={{ fontSize: 40, mb: 1, opacity: 0.3, display: 'block', mx: 'auto' }} />
                      Nenhum log de sistema encontrado.<br />Os logs aparecerão aqui à medida que o sistema for usado.
                    </TableCell></TableRow>
                  ) : sysLogs.map((log) => (
                    <TableRow key={log.id} hover
                      sx={log.nivel === 'ERROR' || log.nivel === 'CRITICAL' ? { bgcolor: 'error.50' }
                        : log.nivel === 'WARN' ? { bgcolor: 'warning.50' } : {}}>
                      <TableCell>
                        <Chip
                          icon={NIVEL_ICON[log.nivel] as any}
                          label={log.nivel}
                          size="small"
                          color={(NIVEL_COLOR[log.nivel] || 'default') as any}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={log.modulo} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 500, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={log.evento} placement="top"><span>{log.evento}</span></Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                        <Tooltip title={log.mensagem || ''} placement="top"><span>{log.mensagem || '—'}</span></Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{log.user_id ? log.user_id.substring(0, 12) + '…' : '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{log.ip_address || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {log.duracao_ms != null ? `${log.duracao_ms} ms` : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(log.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={sysTotal} page={sysPage}
              onPageChange={(_, p) => setSysPage(p)} rowsPerPage={sysRows}
              onRowsPerPageChange={e => { setSysRows(parseInt(e.target.value)); setSysPage(0); }}
              rowsPerPageOptions={[25, 50, 100, 200]} labelRowsPerPage="Linhas:" />
          </Paper>
        </Box>
      )}

      {/* ══════ TAB 5: E-COMMERCE LOGS ══════ */}
      {tab === 5 && (
        <Box>
          {/* Filtros */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" placeholder="OrderID, referência, email, nome..."
              value={ecSearch} onChange={e => { setEcSearch(e.target.value); setEcPage(0); }}
              sx={{ minWidth: 260 }} />
            <TextField size="small" select label="Estado" value={ecStatus}
              onChange={e => { setEcStatus(e.target.value); setEcPage(0); }} sx={{ minWidth: 130 }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="PAID">Pago</MenuItem>
              <MenuItem value="PENDING">Pendente</MenuItem>
              <MenuItem value="FAILED">Falhado</MenuItem>
              <MenuItem value="CANCELLED">Cancelado</MenuItem>
            </TextField>
            <TextField size="small" placeholder="Entidade (ex: CVMA)"
              value={ecEntidade} onChange={e => { setEcEntidade(e.target.value); setEcPage(0); }}
              sx={{ minWidth: 150 }} />
            <TextField size="small" type="date" label="De" InputLabelProps={{ shrink: true }}
              value={ecStart} onChange={e => { setEcStart(e.target.value); setEcPage(0); }} sx={{ minWidth: 140 }} />
            <TextField size="small" type="date" label="Até" InputLabelProps={{ shrink: true }}
              value={ecEnd} onChange={e => { setEcEnd(e.target.value); setEcPage(0); }} sx={{ minWidth: 140 }} />
            <Button size="small" variant="outlined" onClick={() => { setEcSearch(''); setEcStatus(''); setEcEntidade(''); setEcStart(''); setEcEnd(''); setEcPage(0); }}>
              Limpar
            </Button>
          </Box>

          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 600 }}>OrderID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Entidade</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Valor</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Erro SISP</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Webhook</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ecLoading ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                  ) : ecLogs.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sem registos</TableCell></TableRow>
                  ) : ecLogs.map((row: any) => {
                    const statusColor: any =
                      row.status === 'PAID'      ? 'success' :
                      row.status === 'FAILED'    ? 'error'   :
                      row.status === 'CANCELLED' ? 'default' : 'warning';
                    const hasError = row.sispErrorCode && row.sispErrorCode !== 'null';
                    const webhookOk = row.webhookNotified === 'true' || row.webhookNotified === true;
                    const webhookSent = row.notifyUrl && row.notifyUrl !== 'null';
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                          {row.orderId && row.orderId !== 'null' ? row.orderId : row.referencia_interna}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.entidadeNome && row.entidadeNome !== 'null' ? row.entidadeNome : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: 12 }}>
                            {row.customerName && row.customerName !== 'null' ? row.customerName : '—'}
                          </Typography>
                          {row.customerEmail && row.customerEmail !== 'null' && (
                            <Typography variant="caption" color="text.secondary" display="block">{row.customerEmail}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {row.valor_total ? `${fmt(Math.round(Number(row.valor_total)))} CVE` : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={row.status || '?'} color={statusColor} />
                        </TableCell>
                        <TableCell>
                          {hasError ? (
                            <Tooltip title={`${row.sispErrorDescription || ''} ${row.sispErrorDetail || ''}`.trim()}>
                              <Chip size="small" icon={<ErrorOutline />} label={row.sispErrorCode} color="error" variant="outlined" sx={{ cursor: 'help' }} />
                            </Tooltip>
                          ) : row.status === 'PAID' ? (
                            <Chip size="small" icon={<CheckCircle />} label="OK" color="success" variant="outlined" />
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {webhookSent ? (
                            <Tooltip title={row.notifyUrl}>
                              <Chip size="small"
                                icon={webhookOk ? <CheckCircle /> : <Cancel />}
                                label={webhookOk ? 'Enviado' : 'Falhou'}
                                color={webhookOk ? 'success' : 'error'}
                                variant="outlined"
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                          {fmtDate(row.created_at)}
                        </TableCell>
                        <TableCell padding="none">
                          <IconButton size="small" onClick={() => {
                            setSelectedItem({
                              ...row,
                              _isLog: false,
                              sispErrorCode:        row.sispErrorCode !== 'null' ? row.sispErrorCode : null,
                              sispErrorDescription: row.sispErrorDescription !== 'null' ? row.sispErrorDescription : null,
                              sispErrorDetail:      row.sispErrorDetail !== 'null' ? row.sispErrorDetail : null,
                              notifyUrl:            row.notifyUrl !== 'null' ? row.notifyUrl : null,
                            });
                            setDetailDialog(true);
                            setDetailLoading(false);
                          }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={ecTotal} page={ecPage}
              onPageChange={(_, p) => setEcPage(p)} rowsPerPage={ecRows}
              onRowsPerPageChange={e => { setEcRows(parseInt(e.target.value)); setEcPage(0); }}
              rowsPerPageOptions={[25, 50, 100]} labelRowsPerPage="Linhas:" />
          </Paper>
        </Box>
      )}

      {/* ══════ DETAIL DIALOG ══════ */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedItem?._isLog ? 'Detalhe do Log' : 'Detalhe da Transação'}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : selectedItem ? (
            <Box>
              {/* Summary fields */}
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {Object.entries(selectedItem)
                  .filter(([k, v]) => !k.startsWith('_') && typeof v !== 'object' && v !== null && v !== '')
                  .map(([k, v]) => (
                    <Grid key={k} size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10 }}>
                        {k}
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: typeof v === 'number' || k.toLowerCase().includes('ref') || k.toLowerCase().includes('id') ? 'monospace' : 'inherit' }}>
                        {String(v)}
                      </Typography>
                    </Grid>
                  ))}
              </Grid>

              {/* Request body */}
              {(selectedItem.requestBody || selectedItem.REQUEST_BODY) && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Request Body</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 2, maxHeight: 250, overflow: 'auto', bgcolor: 'action.hover' }}>
                    <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {(() => {
                        try { return JSON.stringify(JSON.parse(String(selectedItem.requestBody || selectedItem.REQUEST_BODY)), null, 2); }
                        catch { return String(selectedItem.requestBody || selectedItem.REQUEST_BODY); }
                      })()}
                    </pre>
                  </Paper>
                </>
              )}

              {/* Response body — parse SISP hidden fields */}
              {(selectedItem.responseBody || selectedItem.RESPONSE_BODY) && (() => {
                const raw = String(selectedItem.responseBody || selectedItem.RESPONSE_BODY);
                const fields: Record<string, string> = {};
                const re = /<input[^>]*name=["']([^"']+)["'][^>]*value=["']([^"']*)["'][^>]*>/gi;
                let m;
                while ((m = re.exec(raw)) !== null) fields[m[1]] = m[2];
                const entries = Object.entries(fields);

                if (entries.length === 0) {
                  // Não é HTML com campos SISP — mostrar como texto
                  return (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Resposta SISP</Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 200, overflow: 'auto', bgcolor: 'action.hover' }}>
                        <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                          {raw.substring(0, 2000)}
                        </pre>
                      </Paper>
                    </>
                  );
                }

                const success = fields['merchantResp'] === '0';
                return (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Resposta SISP
                      <Chip
                        label={success ? 'Aprovado' : 'Recusado'}
                        color={success ? 'success' : 'error'}
                        size="small"
                        sx={{ ml: 1, fontSize: 11 }}
                      />
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: success ? 'success.50' : 'error.50' }}>
                      <Grid container spacing={1}>
                        {entries.map(([k, v]) => (
                          <Grid key={k} size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, textTransform: 'uppercase', display: 'block' }}>
                              {k}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: ['merchantResp','messageType','merchantRespErrorCode'].includes(k) ? 700 : 400 }}>
                              {v || '—'}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </>
                );
              })()}
            </Box>
          ) : (
            <Typography color="text.secondary">Sem dados</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SispLogs;
